import { combineReducers } from 'redux';
import { coreReducerMap } from '../reducers/coreReducers';
import { REDUCER_GROUP_LOADERS } from '../reducerGroups';

let storeRef = null;
const asyncReducers = {};
const loadedGroups = new Set();
const inflightGroups = new Map();
let rebuildQueued = false;

export function createRootReducer(extraReducers = {}) {
  return combineReducers({
    ...coreReducerMap,
    ...extraReducers,
  });
}

// The store is built once around ONE persistReducer that wraps this delegating reducer. Injecting a
// group swaps only the inner reducer. Wrapping a fresh persistReducer on every injection (the old
// approach) left the new instance without a persistoid (it is only created on PERSIST), so nothing
// was written to storage after the first lazy group loaded: the selected restaurant, theme, etc.
// silently reverted to stale values on reload.
let currentRootReducer = createRootReducer();
export const delegatingRootReducer = (state, action) => currentRootReducer(state, action);

export function bindInjectStore(store) {
  storeRef = store;
  store.asyncReducers = asyncReducers;
  if (typeof window !== 'undefined') {
    window.__REDUX_INJECT_GROUP__ = injectReducerGroup;
  }
}

function rebuildRootReducer() {
  if (!storeRef) {
    throw new Error('[injectReducer] Store not bound. Call bindInjectStore from configureStore.');
  }
  currentRootReducer = createRootReducer(asyncReducers);
  // Same (persist-wrapped) reducer instance: keeps redux-persist's persistoid alive while the
  // REPLACE action initialises the newly injected slices.
  storeRef.replaceReducer(storeRef.persistedRootReducer);
}

function queueRebuildRootReducer() {
  if (rebuildQueued) return;
  rebuildQueued = true;
  Promise.resolve().then(() => {
    rebuildQueued = false;
    rebuildRootReducer();
  });
}

/**
 * Load a named feature reducer group and merge it into the store (idempotent).
 * @param {string} groupId
 * @returns {Promise<void>}
 */
export async function injectReducerGroup(groupId) {
  if (loadedGroups.has(groupId)) {
    return;
  }
  if (inflightGroups.has(groupId)) {
    return inflightGroups.get(groupId);
  }

  const loader = REDUCER_GROUP_LOADERS[groupId];
  if (!loader) {
    throw new Error(`[injectReducer] Unknown reducer group: ${groupId}`);
  }

  const promise = (async () => {
    const reducers = await loader();
    Object.assign(asyncReducers, reducers);
    loadedGroups.add(groupId);
    queueRebuildRootReducer();
  })().finally(() => {
    inflightGroups.delete(groupId);
  });

  inflightGroups.set(groupId, promise);
  return promise;
}

export function hasReducerGroup(groupId) {
  return loadedGroups.has(groupId);
}

/** Inject several groups in parallel (coalesced replaceReducer). */
export async function injectReducerGroups(groupIds) {
  await Promise.all(groupIds.map((id) => injectReducerGroup(id)));
  // Flush queued replaceReducer (microtask) before callers render connected trees.
  await Promise.resolve();
  if (rebuildQueued) {
    await Promise.resolve();
  }
}
